import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import Pager from '../components/Pager';
import StatusInfo from '../components/StatusInfo';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const MONTHS = ['', 'январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const LIMIT = 20;

const INVOICE_STATUS = {
  forming: { label: 'Счёт формируется', badge: s => s.badgeGray, tip: 'Бухгалтер Refero сверяет данные за этот месяц — счёт ещё не сформирован для отправки.' },
  invoiced: { label: 'Выставлен', badge: s => s.badgeYellow, tip: 'Счёт сформирован и проверен бухгалтером Refero, ожидает оплаты.' },
  paid: { label: 'Оплачено', badge: s => s.badgeTeal, tip: 'Вы оплатили этот счёт — спасибо!' },
};

export default function Commissions() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'months' ? 'months' : 'doctors');
  const headers = { Authorization: 'Bearer ' + localStorage.getItem('clinicToken') };

  return (
    <Layout>
      <h1 style={s.h1}>Комиссии</h1>
      <div style={s.tabs}>
        <button style={{ ...s.tabBtn, ...(tab === 'doctors' ? s.tabBtnActive : {}) }} onClick={() => setTab('doctors')}>По врачам</button>
        <button style={{ ...s.tabBtn, ...(tab === 'months' ? s.tabBtnActive : {}) }} onClick={() => setTab('months')}>По месяцам</button>
      </div>
      {tab === 'doctors' ? <ByDoctorsTab headers={headers} /> : <ByMonthsTab headers={headers} />}
    </Layout>
  );
}

function ByDoctorsTab({ headers }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [periods, setPeriods] = useState([]);
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('');
  const [sort, setSort] = useState('period');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    axios.get(`${API}/clinic-cabinet/payouts/periods`, { headers }).then(r => setPeriods(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const fetchData = useCallback(() => {
    const [year, month] = period ? period.split('-').map(Number) : [undefined, undefined];
    const params = { search: search || undefined, year, month, sort, order, limit: LIMIT, offset };
    axios.get(`${API}/clinic-cabinet/payouts`, { headers, params })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); setSummaryTotal(r.data.summary_total); }).catch(() => {});
    // eslint-disable-next-line
  }, [search, period, sort, order, offset]);

  useEffect(() => { const t = setTimeout(fetchData, 250); return () => clearTimeout(t); }, [fetchData]);

  const toggleSort = (f) => {
    setOffset(0);
    if (sort === f) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSort(f); setOrder('asc'); }
  };
  const arrow = (f) => (sort !== f ? '' : order === 'asc' ? ' ▲' : ' ▼');

  const isFiltered = !!(search.trim() || period);
  const cols = '1.4fr 1.8fr 1.1fr 1fr 1fr 1fr';

  return (
    <>
      <div style={s.summary}>Начислено{isFiltered ? ' (с фильтром)' : ' за всё время'}: <b>{fmtMoney(summaryTotal)} ₽</b> <span style={s.muted}>(врачам + комиссия Refero)</span></div>
      <div style={s.filterBar}>
        <input style={s.input} placeholder="Поиск по врачу" value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }} />
        <select style={s.select} value={period} onChange={e => { setPeriod(e.target.value); setOffset(0); }}>
          <option value="">Все периоды</option>
          {periods.map(p => <option key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>{MONTHS[p.month]} {p.year}</option>)}
        </select>
      </div>
      <div style={s.table}>
        <div style={{ ...s.head, gridTemplateColumns: cols }}>
          <div style={s.thClickable} onClick={() => toggleSort('period')}>Период{arrow('period')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('doctor')}>Врач{arrow('doctor')}</div>
          <div>ID врача</div>
          <div style={s.thClickable} onClick={() => toggleSort('to_doctor')}>Врачу{arrow('to_doctor')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('refero_fee')}>Refero{arrow('refero_fee')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('total')}>Итого{arrow('total')}</div>
        </div>
        {items.map((r, i) => (
          <div key={i} style={{ ...s.row, gridTemplateColumns: cols }}>
            <div>{MONTHS[r.period_month]} {r.period_year}</div>
            <div style={s.strong}>{r.doctor_name}</div>
            <div style={s.refId}>{r.doctor_public_id || '—'}</div>
            <div>{fmtMoney(r.to_doctor)} ₽</div>
            <div style={{ color: '#f59e0b' }}>{fmtMoney(r.refero_fee)} ₽</div>
            <div style={s.money}>{fmtMoney(r.total)} ₽</div>
          </div>
        ))}
        {items.length === 0 && <div style={s.empty}>{total === 0 ? 'Комиссий пока нет' : 'Ничего не найдено'}</div>}
      </div>
      <Pager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
    </>
  );
}

function ByMonthsTab({ headers }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState('period');
  const [order, setOrder] = useState('desc');
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(() => {
    const params = { sort, order, limit: LIMIT, offset };
    axios.get(`${API}/clinic-cabinet/payouts/months`, { headers, params })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); }).catch(() => {});
    // eslint-disable-next-line
  }, [sort, order, offset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSort = (f) => {
    setOffset(0);
    if (sort === f) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSort(f); setOrder('asc'); }
  };
  const arrow = (f) => (sort !== f ? '' : order === 'asc' ? ' ▲' : ' ▼');

  const download = async (invoiceId, e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await axios.get(`${API}/clinic-cabinet/invoices/${invoiceId}/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'invoice.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // счёт не найден/недоступен — молча игнорируем, кнопка и так не должна была быть видна
    } finally {
      setBusy(false);
    }
  };

  const cols = '1.2fr 1fr 1fr 1fr 1fr 1.2fr 1fr';

  return (
    <div style={s.table}>
      <div style={{ ...s.head, gridTemplateColumns: cols }}>
        <div style={s.thClickable} onClick={() => toggleSort('period')}>Период{arrow('period')}</div>
        <div>Первичные</div>
        <div>Повторные</div>
        <div>Refero</div>
        <div style={s.thClickable} onClick={() => toggleSort('total')}>Итого{arrow('total')}</div>
        <div>
          Статус
          <StatusInfo title="Статус счёта" items={Object.entries(INVOICE_STATUS).map(([, v]) => [v.label, v.tip])} />
        </div>
        <div>Действие</div>
      </div>
      {items.map((r, i) => {
        const st = INVOICE_STATUS[r.status] || INVOICE_STATUS.forming;
        return (
          <div key={i} style={{ ...s.row, gridTemplateColumns: cols }}>
            <div style={s.strong}>{MONTHS[r.period_month]} {r.period_year}</div>
            <div>{fmtMoney(r.primary_amount)} ₽</div>
            <div>{fmtMoney(r.repeat_amount)} ₽</div>
            <div style={{ color: '#f59e0b' }}>{fmtMoney(r.refero_fee)} ₽</div>
            <div style={s.money}>{fmtMoney(r.total)} ₽</div>
            <div><span style={st.badge(s)}>{st.label}</span></div>
            <div>
              <button
                style={{ ...s.btnGhostSmall, ...(!['invoiced', 'paid'].includes(r.status) ? s.btnDisabled : {}) }}
                disabled={busy || !['invoiced', 'paid'].includes(r.status)}
                onClick={(e) => download(r.invoice_id, e)}
              >Скачать счёт</button>
            </div>
          </div>
        );
      })}
      {items.length === 0 && <div style={s.empty}>Пока нет завершённых месяцев с начислениями</div>}
      <div style={{ padding: '0 20px 20px' }}>
        <Pager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
      </div>
    </div>
  );
}

const s = {
  h1: { color: '#1B2B4B', fontSize: 24, fontWeight: 700, marginBottom: 12 },
  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tabBtn: { background: '#fff', color: '#64748b', border: '1px solid #d7dee8', padding: '8px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  tabBtnActive: { background: '#1B2B4B', color: '#fff', border: '1px solid #1B2B4B' },
  summary: { color: '#1B2B4B', fontSize: 15, background: '#e9faf7', borderRadius: 10, padding: '12px 16px', marginBottom: 20 },
  filterBar: { display: 'flex', gap: 8, marginBottom: 16 },
  input: { flex: 1, maxWidth: 320, background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' },
  select: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' },
  table: { background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 16px rgba(27,43,75,0.05)' },
  head: { display: 'grid', padding: '14px 20px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #eef2f7', alignItems: 'center' },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #f4f7fb', fontSize: 14, color: '#1B2B4B', alignItems: 'center' },
  strong: { fontWeight: 600 },
  refId: { fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#94a3b8' },
  money: { color: '#149e90', fontWeight: 600 },
  muted: { color: '#94a3b8', fontSize: 13 },
  badgeTeal: { background: '#e9faf7', color: '#149e90', padding: '3px 10px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeGray: { background: '#f1f5f9', color: '#64748b', padding: '3px 10px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeYellow: { background: '#fef3c7', color: '#b45309', padding: '3px 10px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  btnGhostSmall: { background: 'transparent', color: '#149e90', border: '1px solid #149e90', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDisabled: { color: '#cbd5e1', border: '1px solid #e2e8f0', cursor: 'default' },
  empty: { color: '#94a3b8', textAlign: 'center', padding: '40px 0' },
};
